import { sessionMinutes, to24h, type SignCheck } from '@ehr/shared';
import { localToday } from './tpPlans.js';

/**
 * ehr#732 (MEDICATION_MANAGEMENT_NOTE_10-30-V5 V3, Jay 19 B and 20 A): End time checks. Every
 * note keeps "End time is before Start time" (an End at or before the Start). On a Medication
 * management note the End box also turns red, the Duration box says what is wrong and sign-off
 * waits when the End is the same as the Start, more than 4 hours after it, or later than now on
 * today's note. Save Draft still works. Pure, so the editor and the tests read the same rules.
 */
export const END_CHECK_KINDS: readonly string[] = ['Medication management'];
/** 20 A: the longest session, 4 hours. */
export const MAX_SESSION_MIN = 240;

export type EndProblem = 'before' | 'same' | 'long' | 'late';

/** What the Duration box says. */
export const END_PROBLEM_TEXT: Record<EndProblem, string> = {
  before: 'End time is before Start time',
  same: 'End time is the same as Start time',
  long: 'End time is more than 4 hours after Start time',
  late: 'End time is later than now',
};

/** "3:20 PM" as minutes after midnight; null when it is not a time. */
function clockMin(t: string): number | null {
  const m = /^(\d{2}):(\d{2})$/.exec(to24h(t));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
}

/** What is wrong with the End time, if anything (null: nothing, or no End yet). */
export function endProblem(o: {
  kind: string;
  /** The note's date, 'YYYY-MM-DD'. */
  date: string;
  start: string;
  end: string;
  now: Date;
}): EndProblem | null {
  if (!o.start.trim() || !o.end.trim()) return null;
  const m = sessionMinutes(o.start, o.end);
  if (m === null) return null;
  const checked = END_CHECK_KINDS.includes(o.kind);
  if (m < 0 || (m === 0 && !checked)) return 'before';
  if (!checked) return null;
  if (m === 0) return 'same';
  if (m > MAX_SESSION_MIN) return 'long';
  const end = clockMin(o.end);
  const now = o.now.getHours() * 60 + o.now.getMinutes();
  if (o.date === localToday(o.now) && end !== null && end > now) return 'late';
  return null;
}

/**
 * The sign-off items these checks add (What's left), past the shared "End time after the start
 * time" that every note has: "End time within 4 hours of the start" and "End time no later than
 * now". In the times group.
 */
export function endSignChecks(p: EndProblem | null): SignCheck[] {
  if (p === 'long') return [{ label: 'End time within 4 hours of the start', target: 'session' }];
  if (p === 'late') return [{ label: 'End time no later than now', target: 'session' }];
  return [];
}
